import React from 'react'
import { FunctionComponent } from 'react'

import { AnimatePresence, Variants, motion } from 'framer-motion'
import { Redirect, Route, Switch } from 'react-router-dom'

import LoginPage from '../../containers/LoginPage'
import { SearchPage } from '../../containers/SearchPage'
import { Settings } from '../../containers/Settings'
import { ShiftsPage } from '../../containers/Shifts'
import { CompletedTowingJobsPage, TowingJobsPage } from '../../containers/TowingJobsPage'
import { AddTowingOrder } from '../../containers/TowingOrderPage/AddTowingOrder'
import { EditTowingOrder } from '../../containers/TowingOrderPage/EditTowingOrder'
import { ViewTowingOrder } from '../../containers/TowingOrderPage/ViewTowingOrder'
import { AddTowingRecord } from '../../containers/TowingRecordPage/AddTowingRecord'
import { EditTowingRecord } from '../../containers/TowingRecordPage/EditTowingRecord'
import { ViewTowingRecord } from '../../containers/TowingRecordPage/ViewTowingRecord'
import { NavigationMenu } from '../NavigationMenu'

import PrivateRoute from './PrivateRoute'
import styled from 'styled-components'
import { useTheme } from '../../containers/ThemeProvider'
import MfaPage from '../../containers/MfaPage'
import { useApolloClient } from '@apollo/client/react'

interface Props {
  authenticated: boolean
  mfaPending: boolean
  location: any
}

export const Routes: FunctionComponent<Props> = ({ authenticated, location, mfaPending }) => {
  const client = useApolloClient()

  return (
    <Overlay>
      {authenticated && !mfaPending && <NavigationMenu client={client} />}
      <AnimatePresence mode="wait">
        <RoutesContainer key={location.pathname}>
          <Switch location={location}>
            <Route path="/login" render={() => (authenticated ? <Redirect to="/towing-jobs" /> : <LoginPage />)} />
            <Route path="/" exact render={() => (authenticated ? <Redirect to="/towing-jobs" /> : <LoginPage />)} />
            <Route
              path="/mfa"
              exact
              render={() => mfaPending ? <MfaPage /> : <Redirect to="/towing-jobs" />}
            />
            <PrivateRoute
              path="/towing-jobs"
              exact
              authenticated={authenticated}
              component={TowingJobsPage}
              client={client}
            />
            <PrivateRoute
              path="/towing-jobs/completed"
              exact
              authenticated={authenticated}
              component={CompletedTowingJobsPage}
              client={client}
            />
            <PrivateRoute
              path="/settings"
              exact
              authenticated={authenticated}
              component={Settings}
              client={client}
            />
            <PrivateRoute
              path="/towing-record/add"
              exact
              authenticated={authenticated}
              component={AddTowingRecord}
              client={client}
            />

            <PrivateRoute
              path="/towing-record/:id/edit"
              exact
              authenticated={authenticated}
              component={EditTowingRecord}
              client={client}
            />
            <PrivateRoute
              path="/towing-record/:id/edit/:scrollTo"
              exact
              authenticated={authenticated}
              component={EditTowingRecord}
              client={client}
            />
            <PrivateRoute
              path="/towing-record/:id/edit/:scrollTo/:paymentType"
              exact
              authenticated={authenticated}
              component={EditTowingRecord}
              client={client}
            />
            <PrivateRoute
              path="/towing-record/:id/:action"
              exact
              authenticated={authenticated}
              component={ViewTowingRecord}
              client={client}
            />
            <PrivateRoute
              path="/towing-record/:id"
              exact
              authenticated={authenticated}
              component={ViewTowingRecord}
              client={client}
            />
            <PrivateRoute
              path="/towing-order/add"
              exact
              authenticated={authenticated}
              component={AddTowingOrder}
              client={client}
            />
            <PrivateRoute
              path="/towing-order/add/:action"
              exact
              authenticated={authenticated}
              component={AddTowingOrder}
              client={client}
            />
            <PrivateRoute
              path="/towing-order/:id"
              exact
              authenticated={authenticated}
              component={ViewTowingOrder}
              client={client}
            />
            <PrivateRoute
              path="/towing-order/:id/edit/:scrollTo"
              authenticated={authenticated}
              component={EditTowingOrder}
              client={client}
            />
            <PrivateRoute
              path="/towing-order/:id/edit"
              authenticated={authenticated}
              component={EditTowingOrder}
              client={client}
            />
            <PrivateRoute
              path="/search"
              exact
              authenticated={authenticated}
              component={SearchPage}
              client={client}
            />
            <PrivateRoute
              path="/shifts"
              exact
              authenticated={authenticated}
              component={ShiftsPage}
              client={client}
            />

            <Redirect from="/" to="/towing-jobs" />
          </Switch>
        </RoutesContainer>
      </AnimatePresence>
    </Overlay>
  )
}

const routesContainerVariants: Variants = {
  enter: { opacity: 1, transition: { delay: 0.1 } },
  exit: { opacity: 0 },
}

const StyledRoutesContainer = styled(motion.div)``

const RoutesContainer = ({ children, ...props }: React.ComponentProps<typeof StyledRoutesContainer>) => (
  <StyledRoutesContainer initial="exit" animate="enter" exit="exit" variants={routesContainerVariants} {...props}>
    {children}
  </StyledRoutesContainer>
)
//@ important! overflow: auto => to allow scrolling
const StyledOverlay = styled.div<{ visible: boolean }>`
  background-color: ${props => (props.visible ? 'rgba(0, 0, 0, 0.90)' : 'rgba(0, 0, 0, 0);')};
  position: fixed;
  overflow: auto;
  width: 100%;
  height: 100%;
`
const Overlay = ({ children }: { children?: React.ReactNode }) => {
  const { currentTheme } = useTheme()
  return <StyledOverlay visible={currentTheme === 'dark'}>{children}</StyledOverlay>
}
